import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { errorInterceptor } from '../services/ErrorInterceptor';

/**
 * Hook to set up error interceptor with navigation
 */
export const useErrorInterceptor = () => {
    const navigate = useNavigate();

    useEffect(() => {
        // Set the navigate function in the error interceptor
        errorInterceptor.setNavigate(navigate);
    }, [navigate]);

    /**
     * Handle errors using the error interceptor
     */
    const handleError = (error: any) => {
        errorInterceptor.handleError(error);
    };

    /**
 * Check if an error is an authentication error
 */
    const isAuthenticationError = (error: any): boolean => {
        if (!error) return false;

        // Firebase Auth errors
        if (error.code) {
            const authErrorCodes = [
                'auth/user-not-found',
                'auth/wrong-password',
                'auth/invalid-credential',
                'auth/user-disabled',
                'auth/too-many-requests',
                'auth/operation-not-allowed',
                'auth/invalid-email',
                'auth/weak-password',
                'auth/email-already-in-use',
                'auth/requires-recent-login',
                'auth/credential-already-in-use'
            ];

            // Exclude network-related errors that shouldn't trigger login redirect
            const networkErrorCodes = [
                'auth/network-request-failed',
                'auth/too-many-requests'
            ];

            if (networkErrorCodes.includes(error.code)) {
                return false;
            }

            return authErrorCodes.includes(error.code);
        }

        // HTTP status codes
        if (error.status) {
            return error.status === 401 || error.status === 403;
        }

        // Error messages
        if (error.message) {
            const authErrorMessages = [
                'unauthenticated',
                'unauthorized',
                'authentication failed',
                'invalid token',
                'token expired',
                'session expired',
                'user must be authenticated'
            ];
            return authErrorMessages.some(msg =>
                error.message.toLowerCase().includes(msg.toLowerCase())
            );
        }

        return false;
    };

    return {
        handleError,
        isAuthenticationError
    };
};
